import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { createMutationAtom } from 'atoms/create-resource-atom';
import { createResourceAtom } from 'lib/atoms';
import { formatResource } from 'utils/misc';
import {
  compositeRefreshTriggerAtom,
  geometryRefreshAtom,
  toastAtom,
} from 'pages/type-curves/atoms';
import type {
  SlotComposite,
  SlotCompositeErrorDetails,
  SlotCompositeMergeResponse,
  SlotCompositeResponse,
  SlotCompositeUnmergeResponse,
  SlotCompositesIndexResponse,
} from 'types/api-responses';
import type { SlotCompositeBody } from 'types/api-payloads';
import type { FailedApiResponse } from 'components/well-page-content/types';
import { useUnitBuilder } from './context';

const previewAtom = createMutationAtom<
  SlotCompositeResponse,
  'slotCompositePreview'
>();
const createAtom = createMutationAtom<
  SlotCompositeMergeResponse,
  'slotCompositeCreate'
>();
const deleteAtom = createMutationAtom<
  SlotCompositeUnmergeResponse,
  'slotCompositeDelete'
>();

const REASON_TEXT: Record<string, string> = {
  SLOT_NOT_FOUND: 'a slot no longer exists — reload the unit',
  NOT_LTD: 'only LTDs can be merged',
  NO_GEOMETRY: 'an LTD has no geometry',
  ALREADY_IN_COMPOSITE:
    'an LTD is already part of a composite — split it first',
  DIFFERENT_UNIT: 'all LTDs must be in the same unit',
  MIN_TWO_LEGS: 'select two or more LTDs',
  DUPLICATE_LEG: 'the same LTD is selected twice',
  LANDING_REQUIRED: 'choose a landing point',
  BOTTOM_HOLE_REQUIRED: 'choose a bottom hole',
  LANDING_NOT_IN_LEGS: 'the landing point is not on a selected LTD',
  BOTTOM_HOLE_NOT_IN_LEGS: 'the bottom hole is not on a selected LTD',
  SAME_LEG_BOTH_ENDS: 'landing point and bottom hole must be on different LTDs',
  INVALID_END: 'an end is not valid',
  INCONSISTENT_BOTTOM_HOLE: 'the well cannot finish on that end',
  DEGENERATE_TANGENT: 'two LTDs share an endpoint',
};

export interface CompositeError {
  message: string;
  expectedBottomHole: SlotCompositeErrorDetails['expectedBottomHole'] | null;
}

/**
 * A composite write error in plain words.
 *
 * @param {FailedApiResponse} err - The error body, if any.
 * @return {CompositeError} The message and any suggested bottom hole.
 */
export const describeCompositeError = (
  err?: FailedApiResponse
): CompositeError => {
  const first = err?.errors?.[0];
  if (first?.code === 'WORKSPACE_PROMOTED') {
    return {
      message: 'This workspace has been promoted; composites are frozen.',
      expectedBottomHole: null,
    };
  }
  const details = (first?.details ?? {}) as SlotCompositeErrorDetails;
  const reasons = [
    ...new Set(
      (details.ineligible ?? []).map(
        (i) => REASON_TEXT[i.reason ?? ''] ?? i.reason ?? 'not eligible'
      )
    ),
  ];
  return {
    message: reasons.length
      ? `Cannot merge: ${reasons.join('; ')}.`
      : (first?.message ?? 'The request failed.'),
    expectedBottomHole: details.expectedBottomHole ?? null,
  };
};

/**
 * The scoped unit's composites, refetched after a merge or split and whenever
 * slot geometry is refreshed (Update Geometries, Generate, rotate).
 *
 * @return {object} `{ composites, loading }`.
 */
export const useUnitComposites = (): {
  composites: SlotComposite[];
  loading: boolean;
} => {
  const { inventoryWorkspaceId, unitId } = useUnitBuilder();
  const listAtom = useMemo(
    () =>
      createResourceAtom<SlotCompositesIndexResponse>(
        unitId === null
          ? undefined
          : `/inventory_workspaces/${inventoryWorkspaceId}/slot_composites?unit_id=${unitId}`,
        'keep-old-data-on-refetch'
      ),
    [inventoryWorkspaceId, unitId]
  );
  const [resource, refetch] = useAtom(listAtom);
  const geometryRefresh = useAtomValue(geometryRefreshAtom);
  const compositeRefresh = useAtomValue(compositeRefreshTriggerAtom);
  const refreshKey = `${geometryRefresh}|${compositeRefresh}`;
  const seenRefresh = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === seenRefresh.current) return;
    seenRefresh.current = refreshKey;
    refetch();
  }, [refreshKey, refetch]);
  const { data } = formatResource(resource);
  return {
    composites: data?.composites ?? [],
    loading: resource.loading,
  };
};

export interface CompositeActions {
  preview: (
    body: SlotCompositeBody,
    onDone: (composite: SlotComposite | null, error?: CompositeError) => void
  ) => void;
  create: (
    body: SlotCompositeBody,
    onDone: (ok: boolean, error?: CompositeError) => void
  ) => void;
  split: (
    composite: { id: number; name: string },
    onDone?: (ok: boolean) => void
  ) => void;
  creating: boolean;
  splitting: boolean;
}

/**
 * Preview, merge and split. Merge and split bump `compositeRefreshTriggerAtom`,
 * which refetches the map listing, the gun barrel, the slot table and the list.
 *
 * @return {CompositeActions} The actions and their in-flight state.
 */
export const useCompositeActions = (): CompositeActions => {
  const { inventoryWorkspaceId } = useUnitBuilder();
  const base = `/inventory_workspaces/${inventoryWorkspaceId}/slot_composites`;
  const [, runPreview] = useAtom(previewAtom);
  const [{ loading: creating }, runCreate] = useAtom(createAtom);
  const [{ loading: splitting }, runDelete] = useAtom(deleteAtom);
  const setToast = useSetAtom(toastAtom);
  const bumpRefresh = useSetAtom(compositeRefreshTriggerAtom);

  const preview = useCallback<CompositeActions['preview']>(
    (body, onDone) =>
      runPreview({
        url: `${base}/preview`,
        type: 'POST',
        data: { composite: body },
        silentLoading: true,
        onSuccess: (data) => onDone(data?.composite ?? null),
        onError: (err) => onDone(null, describeCompositeError(err)),
      }),
    [base, runPreview]
  );

  const create = useCallback<CompositeActions['create']>(
    (body, onDone) =>
      runCreate({
        url: base,
        type: 'POST',
        data: { composite: body },
        onSuccess: (data) => {
          setToast({
            kind: 'success',
            title: `Merged ${
              data?.composite.legCount ?? ''
            } LTD geometries into ${data?.composite.name ?? 'a composite'}`,
          });
          bumpRefresh((n) => n + 1);
          onDone(true);
        },
        onError: (err) => onDone(false, describeCompositeError(err)),
      }),
    [base, runCreate, setToast, bumpRefresh]
  );

  const split = useCallback<CompositeActions['split']>(
    ({ id, name }, onDone) =>
      runDelete({
        url: `${base}/${id}`,
        type: 'DELETE',
        data: null,
        onSuccess: (data) => {
          setToast({
            kind: 'success',
            title: `Split ${name} back into ${
              data?.slots?.length ?? ''
            } LTD geometries`,
          });
          bumpRefresh((n) => n + 1);
          onDone?.(true);
        },
        onError: (err) => {
          setToast({
            kind: 'error',
            title: 'Split failed',
            subtitle: describeCompositeError(err).message,
          });
          onDone?.(false);
        },
      }),
    [base, runDelete, setToast, bumpRefresh]
  );

  return { preview, create, split, creating, splitting };
};
